import React, { useEffect, useRef, useState } from "react";
import styles from "./PdfAttach.module.css";
import {
  deleteFileFromIndexedDB,
  getFilesFromIndexedDB,
  saveFilesToIndexedDB,
} from "./indexedDbhelper";

const PdfAttach = ({ onFilesSelect, storageKey, totalFiles, maxFiles }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);

  const myFileCountRef = useRef(0);
  const otherComponentsFileCountRef = useRef(0);
  const initialLoadDone = useRef(false);

  const isUploadDisabled = totalFiles >= maxFiles;

  useEffect(() => {
    otherComponentsFileCountRef.current = totalFiles - myFileCountRef.current;
  }, [totalFiles]);

  useEffect(() => {
    if (initialLoadDone.current) return;

    const loadSavedFiles = async () => {
      setIsLoading(true);
      try {
        const storedMetadata = localStorage.getItem(`metadata-${storageKey}`);

        if (storedMetadata) {
          const metadata = JSON.parse(storedMetadata);

          // Retrieve actual files from IndexedDB
          const filesWithBlobs = await getFilesFromIndexedDB(
            storageKey,
            metadata
          );

          if (filesWithBlobs.length > 0) {
            // Add the componentKey to each file
            const filesWithKeys = filesWithBlobs.map((file) => {
              if (!file.componentKey) {
                Object.defineProperty(file, "componentKey", {
                  value: storageKey,
                  enumerable: true,
                  configurable: true,
                });
              }
              return file;
            });

            setSelectedFiles(filesWithKeys);
            onFilesSelect(filesWithKeys, storageKey);

            // Update our file count reference
            myFileCountRef.current = filesWithKeys.length;
            // After we load files, recalculate what other components have contributed
            otherComponentsFileCountRef.current =
              totalFiles - filesWithKeys.length;
          } else if (metadata.length > 0) {
            console.warn("Files in metadata not found in IndexedDB");
            localStorage.removeItem(`metadata-${storageKey}`);
          }
        }
      } catch (error) {
        console.error("Error loading files:", error);
        localStorage.removeItem(`metadata-${storageKey}`);
      } finally {
        setIsLoading(false);
        initialLoadDone.current = true;
      }
    };

    loadSavedFiles();
  }, [storageKey, totalFiles, onFilesSelect]);

  const handleFileChange = async (event) => {
    const newFiles = Array.from(event.target.files);
    if (!newFiles.length) return;

    // Calculate the total count with this new batch precisely
    const otherFilesCount = otherComponentsFileCountRef.current;
    const myCurrentFilesCount = myFileCountRef.current;
    const newTotalCount =
      otherFilesCount + myCurrentFilesCount + newFiles.length;

    // Check against max with our precise count
    if (newTotalCount > maxFiles) {
      alert(`Možete učitati najviše ${maxFiles} dokumenata ukupno.`);
      return;
    }

    try {
      setIsLoading(true);

      // Add component key to each file
      const newFilesWithKey = newFiles.map((file) => {
        if (!file.componentKey) {
          Object.defineProperty(file, "componentKey", {
            value: storageKey,
            enumerable: true,
            configurable: true,
          });
        }
        return file;
      });

      // Save to IndexedDB and get the files with IDs
      const savedFiles = await saveFilesToIndexedDB(
        storageKey,
        newFilesWithKey
      );

      setSelectedFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, ...savedFiles];

        // Update our reference of how many files we're contributing
        myFileCountRef.current = updatedFiles.length;

        onFilesSelect(updatedFiles, storageKey);
        saveMetadata(updatedFiles);

        return updatedFiles;
      });
    } catch (error) {
      console.error("Error saving files:", error);
      alert("Pogreška pri spremanju datoteka. Molimo pokušajte ponovo.");
    } finally {
      setIsLoading(false);

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const saveMetadata = (files) => {
    try {
      if (!files?.length) {
        localStorage.removeItem(`metadata-${storageKey}`);
        return;
      }
      
      const metadata = files.map((file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        id: file.id, // Use the ID assigned by IndexedDB
      }));

      localStorage.setItem(
        `metadata-${storageKey}`,
        JSON.stringify(metadata)
      );
    } catch (error) {
      console.error("Error saving file metadata:", error);
      if (error.name === "QuotaExceededError") {
        alert(
          "Nema dovoljno prostora za spremanje podataka o datotekama. Pokušajte s manje datoteka."
        );
      }
    }
  };

  const removeFile = async (indexToRemove) => {
    const fileToRemove = selectedFiles[indexToRemove];

    try {
      setIsLoading(true);

      // Delete from IndexedDB
      await deleteFileFromIndexedDB(
        storageKey,
        fileToRemove.id || fileToRemove.name
      );

      // Update state with removed file
      setSelectedFiles((prevFiles) => {
        const updatedFiles = prevFiles.filter(
          (_, index) => index !== indexToRemove
        );

        // Update our reference count
        myFileCountRef.current = updatedFiles.length;

        onFilesSelect(updatedFiles, storageKey);
        saveMetadata(updatedFiles);

        return updatedFiles;
      });
    } catch (error) {
      console.error("Error removing file:", error);
      alert("Pogreška pri brisanju datoteke. Molimo pokušajte ponovo.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.PdfAttachContainer}>
      <span>Priložite PDF</span>

      {isLoading && (
        <div className={styles.loadingIndicator}>Učitavanje datoteka...</div>
      )}

      <form encType="multipart/form-data" name={`pdfDocuments`}>
        <label
          htmlFor={`fileInput${storageKey}`}
          className={`${styles.fileInputLabel} ${
            isUploadDisabled || isLoading ? styles.disabled : ""
          }`}
          title={
            isUploadDisabled
              ? "Dostigli ste maksimalni broj dokumenata"
              : isLoading
              ? "Učitavanje u tijeku..."
              : "Dodaj datoteke"
          }
        >
          {isUploadDisabled
            ? "Dostigli ste limit"
            : isLoading
            ? "Učitavanje..."
            : "Dodaj datoteke"}
        </label>
        <input
          id={`fileInput${storageKey}`}
          name={`pdfDocuments-${storageKey}`}
          type="file"
          onChange={handleFileChange}
          multiple
          accept=".pdf"
          ref={fileInputRef}
          style={{ display: "none" }}
          disabled={isUploadDisabled || isLoading}
        />
      </form>

      {selectedFiles.length > 0 && (
        <div className={styles.filesList}>
          <h4>Odabrani dokumenti:</h4>
          <ul>
            {selectedFiles.map((file, index) => (
              <li
                key={file.id || `${file.name}-${index}`}
                className={styles.fileItem}
              >
                <span className={styles.fileName}>{file.name}</span>
                <button
                  className={styles.removeButton}
                  onClick={() => removeFile(index)}
                  title="Ukloni datoteku"
                  disabled={isLoading}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PdfAttach;
